import React, { useState, useEffect, useCallback } from 'react';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function MCPPanel() {
  const [status, setStatus] = useState({ enabled: false, server_url: null, tools_count: 0 });
  const [serverUrl, setServerUrl] = useState('');
  const [authToken, setAuthToken] = useState('');
  const [tools, setTools] = useState([]);
  const [connecting, setConnecting] = useState(false);
  const [invoking, setInvoking] = useState(null);
  const [toolResult, setToolResult] = useState(null);

  const fetchTools = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/mcp/tools`);
      const data = await res.json();
      setTools(data.tools || []);
    } catch (err) {
      logger.error('Failed to fetch tools:', err);
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/mcp/status`);
      const data = await res.json();
      setStatus(data);
      if (data.enabled) {
        fetchTools();
      }
    } catch (err) {
      logger.error('Failed to fetch MCP status:', err);
    }
  }, [fetchTools]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const connect = async () => {
    if (!serverUrl.trim()) return;
    setConnecting(true);
    try {
      const res = await fetch(`${API_URL}/api/mcp/connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ server_url: serverUrl, auth_token: authToken || null })
      });
      const data = await res.json();
      if (data.success) {
        fetchStatus();
        fetchTools();
      } else {
        alert('Connection failed: ' + (data.detail || 'Unknown error'));
      }
    } catch (err) {
      alert('Connection failed: ' + err.message);
    }
    setConnecting(false);
  };

  const disconnect = async () => {
    try {
      await fetch(`${API_URL}/api/mcp/disconnect`, { method: 'POST' });
      setStatus({ enabled: false, server_url: null, tools_count: 0 });
      setTools([]);
    } catch (err) {
      logger.error('Failed to disconnect:', err);
    }
  };

  const invokeTool = async (toolName, args = {}) => {
    setInvoking(toolName);
    setToolResult(null);
    try {
      const res = await fetch(`${API_URL}/api/mcp/invoke`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tool_name: toolName, arguments: args })
      });
      const data = await res.json();
      setToolResult({ tool: toolName, ...data });
    } catch (err) {
      setToolResult({ tool: toolName, success: false, error: err.message });
    }
    setInvoking(null);
  };

  return (
    <div className="mcp-panel" data-testid="mcp-panel">
      <div className="mcp-header">
        <h2>📱 MCP Phone Integration</h2>
        <span className={`mcp-status ${status.enabled ? 'connected' : 'disconnected'}`}>
          {status.enabled ? '● Connected' : '○ Disconnected'}
        </span>
      </div>

      {!status.enabled ? (
        <div className="mcp-connect-form">
          <p className="mcp-description">
            Connect to your phone's MCP server to enable AI control of your device.
            Supports file access, notifications, clipboard, location, and more.
          </p>
          <div className="form-group">
            <label>MCP Server URL</label>
            <input
              type="text"
              value={serverUrl}
              onChange={(e) => setServerUrl(e.target.value)}
              placeholder="http://192.168.1.100:8001"
              data-testid="mcp-server-url"
            />
          </div>
          <div className="form-group">
            <label>Auth Token (optional)</label>
            <input
              type="password"
              value={authToken}
              onChange={(e) => setAuthToken(e.target.value)}
              placeholder="Bearer token if required"
              data-testid="mcp-auth-token"
            />
          </div>
          <button 
            className="mcp-connect-btn"
            onClick={connect}
            disabled={connecting || !serverUrl.trim()}
            data-testid="mcp-connect-btn"
          >
            {connecting ? 'Connecting...' : 'Connect to Phone'}
          </button>
        </div>
      ) : (
        <div className="mcp-connected">
          <div className="mcp-info">
            <p><strong>Server:</strong> {status.server_url}</p>
            <p><strong>Tools Available:</strong> {tools.length}</p>
            <button className="mcp-disconnect-btn" onClick={disconnect} data-testid="mcp-disconnect-btn">
              Disconnect
            </button>
          </div>

          <div className="mcp-tools">
            <h3>Available Tools</h3>
            <div className="tools-grid">
              {tools.map((tool) => (
                <div key={`tool-${tool.name}`} className="tool-card" data-testid={`mcp-tool-${tool.name}`}>
                  <div className="tool-name">{tool.name}</div>
                  <div className="tool-desc">{tool.description || 'No description'}</div>
                  <button
                    className="tool-invoke-btn"
                    onClick={() => invokeTool(tool.name)}
                    disabled={invoking === tool.name}
                  >
                    {invoking === tool.name ? 'Running...' : 'Invoke'}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mcp-quick-actions">
            <h3>Quick Actions</h3>
            <div className="quick-actions-grid">
              <button onClick={() => invokeTool('get_location')} disabled={!!invoking}>
                📍 Get Location
              </button>
              <button onClick={() => invokeTool('get_clipboard')} disabled={!!invoking}>
                📋 Get Clipboard
              </button>
              <button onClick={() => invokeTool('get_system_info')} disabled={!!invoking}>
                ℹ️ System Info
              </button>
              <button onClick={() => invokeTool('take_screenshot')} disabled={!!invoking}>
                📸 Screenshot
              </button>
            </div>
          </div>

          {/* Tool Result */}
          {toolResult && (
            <div className={`tool-result ${toolResult.success ? 'success' : 'error'}`}>
              <h4>Result: {toolResult.tool}</h4>
              <pre>{JSON.stringify(toolResult.result || toolResult.error, null, 2)}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default MCPPanel;
